import { Plus } from "lucide-react";
import {
  InfiniteListBase,
  RecordContextProvider,
  useListContext,
  useTranslate,
} from "ra-core";
import { useState } from "react";
import { Link } from "react-router";

import { CompanyAvatar } from "@/components/atomic-crm/companies/CompanyAvatar";
import { MobileContent } from "@/components/atomic-crm/layout/MobileContent";
import MobileHeader from "@/components/atomic-crm/layout/MobileHeader";
import { InfinitePagination } from "@/components/atomic-crm/misc/InfinitePagination";
import type { Company } from "@/components/atomic-crm/types";
import { Button } from "@/components/ui/button";
import { CompanySearchInput } from "./CompanySearchInput";
import { CompanyCreateSheet } from "./CompanySheets";

/**
 * Organizations on the phone (task 1.12, D-044): a list by name with a name
 * search, a button for a new one, each row opens the organization.
 */
export const CompanyListMobile = () => (
  <InfiniteListBase
    resource="companies"
    perPage={25}
    sort={{ field: "name", order: "ASC" }}
    queryOptions={{
      onError: () => {
        /* the list shows its own error */
      },
    }}
  >
    <CompanyListLayoutMobile />
  </InfiniteListBase>
);

const CompanyListLayoutMobile = () => {
  const translate = useTranslate();
  const [createOpen, setCreateOpen] = useState(false);
  return (
    <div>
      <CompanyCreateSheet open={createOpen} onOpenChange={setCreateOpen} />
      <MobileHeader showRefresh>
        <div className="flex flex-1 items-center justify-between">
          <h1 className="text-xl font-semibold">
            {translate("resources.companies.name", { smart_count: 2 })}
          </h1>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="rounded-full"
            aria-label={translate("resources.companies.action.new")}
            onClick={() => setCreateOpen(true)}
          >
            <Plus className="size-5" />
          </Button>
        </div>
      </MobileHeader>
      <MobileContent>
        <CompanySearchInput />
        <CompanyRowsMobile />
        <div className="flex justify-center">
          <InfinitePagination />
        </div>
      </MobileContent>
    </div>
  );
};

const CompanyRowsMobile = () => {
  const translate = useTranslate();
  const { data, isPending, error } = useListContext<Company>();
  if (isPending) return null;
  if (error && !data) {
    return (
      <p className="py-4 text-center text-muted-foreground">
        {translate("ra.notification.http_error")}
      </p>
    );
  }
  if (!data?.length) {
    return (
      <p className="py-4 text-center text-muted-foreground">
        {translate("tenax.mobile.companies_empty")}
      </p>
    );
  }
  return (
    <ul aria-label={translate("resources.companies.name", { smart_count: 2 })}>
      {data.map((company) => (
        <li key={company.id}>
          <RecordContextProvider value={company}>
            <Link
              to={`/companies/${company.id}/show`}
              className="flex min-h-14 items-center gap-4 py-2 no-underline"
            >
              <CompanyAvatar />
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium">{company.name}</div>
                {company.nb_contacts ? (
                  <div className="text-sm text-muted-foreground">
                    {translate("tenax.mobile.contacts_count", {
                      smart_count: company.nb_contacts,
                    })}
                  </div>
                ) : null}
              </div>
            </Link>
          </RecordContextProvider>
        </li>
      ))}
    </ul>
  );
};
